const DB_NAME = 'chama-pos-catalog-v1';
const memory = new Map();
const inflight = new Map();
const clone = value => value ? JSON.parse(JSON.stringify(value)) : value;

// IndexedDB avoids synchronous multi-megabyte localStorage writes. Storage is
// optional: private mode, blocked upgrades and quota errors use memory only.
export const catalogStorage = {
  async access(key, value) {
    if (typeof indexedDB === 'undefined') return value === undefined ? clone(memory.get(key)) : undefined;
    return new Promise(resolve => {
      let db;
      let finished = false;
      const finish = result => { if (finished) return; finished = true; clearTimeout(timer); db?.close(); resolve(result); };
      const timer = setTimeout(() => finish(undefined), 1500);
      try {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => request.result.createObjectStore('catalogs');
        request.onerror = () => finish(undefined);
        request.onblocked = () => finish(undefined);
        request.onsuccess = () => {
          db = request.result;
          if (finished) { db.close(); return; }
          const tx = db.transaction('catalogs', value === undefined ? 'readonly' : 'readwrite');
          const operation = value === undefined ? tx.objectStore('catalogs').get(key) : tx.objectStore('catalogs').put(value, key);
          let result;
          operation.onsuccess = () => { result = operation.result; };
          tx.oncomplete = () => finish(result);
          tx.onerror = tx.onabort = () => finish(undefined);
        };
      } catch { finish(undefined); }
    });
  },
  async get(key) { return (await this.access(key)) || clone(memory.get(key)); },
  async set(key, value) {
    // Bound fallback memory; IndexedDB retains only the last snapshot per scope.
    memory.delete(key); memory.set(key, clone(value));
    while (memory.size > 4) memory.delete(memory.keys().next().value);
    await this.access(key, value);
  },
};

const validScope = (value, companyId, branchId) => value
  && String(value.companyId) === String(companyId) && String(value.branchId) === String(branchId)
  && typeof value.hash === 'string' && /^[a-f0-9]{64}$/.test(value.hash);
const validCatalog = value => Array.isArray(value?.data) && value.data.every(product => product && product.id != null && Array.isArray(product.Varieties));

export async function loadValidatedPosCatalog({ companyId, branchId, fetchHash, fetchSnapshot, fetchLegacy, onProgress = () => {}, storage = catalogStorage }) {
  if (!companyId) return fetchLegacy();
  const key = `v1:${companyId}:${branchId}`;
  if (inflight.has(key)) {
    onProgress('Esperando el catálogo de esta sucursal…');
    return clone(await inflight.get(key));
  }
  const work = (async () => {
    onProgress('Comprobando si el catálogo cambió…');
    const validationStarted = Date.now();
    let metadata;
    try { metadata = await fetchHash(); }
    catch (error) {
      // Rolling deployments can lack the cache endpoints/migration. Network and
      // authorization failures never authorize a stale local catalog.
      if ([404, 501].includes(error?.response?.status)
        || (error?.response?.status === 503 && error?.response?.data?.code === 'CATALOG_CACHE_UNAVAILABLE')) {
        onProgress('Descargando catálogo…');
        return fetchLegacy();
      }
      throw error;
    }
    if (!validScope(metadata, companyId, branchId)) throw new Error('El catálogo recibido no corresponde a esta empresa y sucursal.');
    const saved = await storage.get(key).catch(() => null);
    const remaining = metadata.promotionClock?.nextBoundaryAt
      ? Date.parse(metadata.promotionClock.nextBoundaryAt) - Date.parse(metadata.promotionClock.serverNow) : Infinity;
    const crossedBoundary = Date.now() - validationStarted >= remaining;
    if (!crossedBoundary && validScope(saved, companyId, branchId) && saved.hash === metadata.hash && validCatalog(saved.catalog)) {
      onProgress('Preparando el catálogo guardado…');
      return { ...saved.catalog, promotionClock: metadata.promotionClock };
    }
    onProgress('Descargando cambios del catálogo…');
    const snapshot = await fetchSnapshot();
    if (!validScope(snapshot, companyId, branchId) || !validCatalog(snapshot.catalog)) throw new Error('El catálogo recibido está incompleto. Intenta nuevamente.');
    // Snapshot carries its own hash: a change between hash and download is safe.
    await storage.set(key, snapshot).catch(() => {});
    onProgress('Preparando productos y precios…');
    return { ...snapshot.catalog, promotionClock: snapshot.promotionClock };
  })();
  inflight.set(key, work);
  try { return clone(await work); } finally { if (inflight.get(key) === work) inflight.delete(key); }
}
